import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';

interface RightPanelContextValue {
  /** Whether a right panel is registered (i.e., the current page has one) */
  isRegistered: boolean;
  /** Whether the right panel is currently open */
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggle: () => void;
  /** Called by pages that have a right panel on mount */
  register: () => void;
  /** Called by pages that have a right panel on unmount */
  unregister: () => void;
}

const RightPanelContext = createContext<RightPanelContextValue | null>(null);

export function RightPanelProvider({ children }: { children: ReactNode }) {
  const isMobile = useIsMobile();
  const [isRegistered, setIsRegistered] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  const register = useCallback(() => {
    setIsRegistered(true);
    setIsOpen(!isMobile);
  }, [isMobile]);

  const unregister = useCallback(() => {
    setIsRegistered(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  return (
    <RightPanelContext.Provider value={{ isRegistered, isOpen, setIsOpen, toggle, register, unregister }}>
      {children}
    </RightPanelContext.Provider>
  );
}

export function useRightPanel() {
  const ctx = useContext(RightPanelContext);
  if (!ctx) throw new Error('useRightPanel must be used within RightPanelProvider');
  return ctx;
}
